import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../../lib/api.js';
import type { IngestedImage, NormalizationMethod } from '../../lib/types.js';
import { StatusBadge } from '../../components/StatusBadge.js';

function fmt(bytes: number): string {
  return bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
    : `${Math.round(bytes / 1024)} KB`;
}

export function IngestedImageDetailPage() {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const [method, setMethod] = useState<NormalizationMethod>('checkerboard');
  const [tolerance, setTolerance] = useState(30);
  const [reviewNotes, setReviewNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['ingested-images', id],
    queryFn: () => api.get<IngestedImage>(`/ingested-images/${id}`),
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['ingested-images', id] });
    await queryClient.invalidateQueries({ queryKey: ['ingested-images'] });
  };

  const normalize = useMutation({
    mutationFn: () =>
      api.post<IngestedImage>(`/ingested-images/${id}/normalize`, { method, tolerance }),
    onSuccess: () => {
      setError(null);
      void invalidate();
    },
    onError: (err) => setError(err instanceof ApiClientError ? err.message : 'Normalize failed'),
  });

  const review = useMutation({
    mutationFn: ({
      decision,
      variant,
    }: {
      decision: 'approved' | 'rejected';
      variant?: 'original' | 'processed';
    }) =>
      api.post<IngestedImage>(`/ingested-images/${id}/review`, {
        decision,
        variant,
        notes: reviewNotes || undefined,
      }),
    onSuccess: () => {
      setError(null);
      void invalidate();
    },
    onError: (err) => setError(err instanceof ApiClientError ? err.message : 'Review failed'),
  });

  if (query.isLoading) return <p>Loading…</p>;
  if (!query.data) {
    return (
      <p role="alert" className="form-error">
        Image not found.
      </p>
    );
  }
  const img = query.data.data;
  const canActOnOriginal = img.status === 'uploaded' || img.status === 'normalized';
  const canApproveOriginalDirect =
    img.status === 'uploaded' &&
    img.analysis.hasTransparentPixels &&
    !img.analysis.checkerboardDetected;
  const canApproveProcessed = img.status === 'normalized' && img.processed !== null;
  const isTerminal = img.status === 'approved' || img.status === 'rejected';

  return (
    <section>
      <p>
        <Link to="/ingested-images">← Back to ingested images</Link>
      </p>
      <div className="page-head">
        <h1>{img.provenance.originalFilename}</h1>
        <StatusBadge status={img.status} />
      </div>

      {error && (
        <p role="alert" className="form-error">
          {error}
        </p>
      )}

      {/* Analysis panel */}
      <div className="card">
        <h2>Analysis</h2>
        <dl className="detail-grid">
          <dt>Alpha channel</dt>
          <dd>{img.analysis.hasAlphaChannel ? '✓ present' : '✗ absent'}</dd>
          <dt>Real transparency</dt>
          <dd>{img.analysis.hasTransparentPixels ? '✓ genuine transparent pixels' : '✗ none'}</dd>
          <dt>Checkerboard</dt>
          <dd>
            {img.analysis.checkerboardDetected ? (
              <span className="badge badge--warn">
                Detected ({Math.round(img.analysis.checkerboardCoverage * 100)}% coverage)
              </span>
            ) : (
              '✓ not detected'
            )}
          </dd>
          <dt>Original size</dt>
          <dd>
            {img.original.width}×{img.original.height} — {fmt(img.original.bytes)}
          </dd>
          <dt>SHA-256</dt>
          <dd>
            <code>{img.original.sha256.slice(0, 24)}…</code>
          </dd>
          <dt>Source</dt>
          <dd>{img.provenance.source}</dd>
          {img.provenance.note && (
            <>
              <dt>Note</dt>
              <dd>{img.provenance.note}</dd>
            </>
          )}
        </dl>
        {img.analysis.warnings.length > 0 && (
          <ul className="warning-list">
            {img.analysis.warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        )}
      </div>

      {/* Original preview */}
      <div className="card">
        <h2>Original</h2>
        {/* CSS checkerboard background makes real transparency visible */}
        <div className="transparency-preview">
          <img
            src={`/api/v1/ingested-images/${id}/original/content`}
            alt="original"
            className="asset-preview"
          />
        </div>
      </div>

      {/* Processed preview (after normalization) */}
      {img.processed && (
        <div className="card">
          <h2>
            Processed ({img.processed.method}, tolerance {img.processed.tolerance})
          </h2>
          <div className="transparency-preview">
            <img
              src={`/api/v1/ingested-images/${id}/processed/content`}
              alt="processed"
              className="asset-preview"
            />
          </div>
          <dl className="detail-grid">
            <dt>Size</dt>
            <dd>
              {img.processed.width}×{img.processed.height} — {fmt(img.processed.bytes)}
            </dd>
            <dt>SHA-256</dt>
            <dd>
              <code>{img.processed.sha256.slice(0, 24)}…</code>
            </dd>
          </dl>
          {img.processed.warnings.length > 0 && (
            <ul className="warning-list">
              {img.processed.warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Normalize controls */}
      {canActOnOriginal && !isTerminal && (
        <div className="card">
          <h2>Normalize background</h2>
          <div className="form-row">
            <label htmlFor="norm-method">Method</label>
            <select
              id="norm-method"
              value={method}
              onChange={(e) => setMethod(e.target.value as NormalizationMethod)}
            >
              <option value="checkerboard">Checkerboard → alpha</option>
              <option value="solid_edge">Solid edge flood-fill</option>
              <option value="both">Both</option>
            </select>
          </div>
          <div className="form-row">
            <label htmlFor="norm-tolerance">Tolerance (0–255)</label>
            <input
              id="norm-tolerance"
              type="number"
              min={0}
              max={255}
              value={tolerance}
              onChange={(e) => setTolerance(Number(e.target.value))}
            />
          </div>
          <button type="button" onClick={() => normalize.mutate()} disabled={normalize.isPending}>
            {normalize.isPending ? 'Processing…' : img.processed ? 'Re-normalize' : 'Normalize'}
          </button>
        </div>
      )}

      {/* Review controls */}
      {!isTerminal && (
        <div className="card">
          <h2>Review</h2>
          <div className="form-row">
            <label htmlFor="review-notes">Notes (optional)</label>
            <textarea
              id="review-notes"
              rows={2}
              value={reviewNotes}
              onChange={(e) => setReviewNotes(e.target.value)}
            />
          </div>
          <div className="button-row">
            {canApproveOriginalDirect && (
              <button
                type="button"
                onClick={() => review.mutate({ decision: 'approved', variant: 'original' })}
                disabled={review.isPending}
              >
                Approve original
              </button>
            )}
            {canApproveProcessed && (
              <button
                type="button"
                onClick={() => review.mutate({ decision: 'approved', variant: 'processed' })}
                disabled={review.isPending}
              >
                Approve processed
              </button>
            )}
            {!canApproveOriginalDirect && !canApproveProcessed && img.status === 'uploaded' && (
              <p className="muted">
                Normalize the background first — direct approval is blocked because the image has a
                rasterized checkerboard or no real transparency.
              </p>
            )}
            <button
              type="button"
              className="button--danger"
              onClick={() => review.mutate({ decision: 'rejected' })}
              disabled={review.isPending}
            >
              Reject
            </button>
          </div>
        </div>
      )}

      {/* Terminal state */}
      {isTerminal && img.review && (
        <div className="card">
          <h2>Review result</h2>
          <dl className="detail-grid">
            <dt>Decision</dt>
            <dd>
              <StatusBadge status={img.review.decision} />
            </dd>
            <dt>Variant</dt>
            <dd>{img.review.variant}</dd>
            {img.review.notes && (
              <>
                <dt>Notes</dt>
                <dd>{img.review.notes}</dd>
              </>
            )}
          </dl>
        </div>
      )}
    </section>
  );
}
